import { useState } from 'react';
import { useIntl } from 'react-intl';
import { Link, Outlet, useLoaderData, useRevalidator, type LoaderFunctionArgs } from 'react-router-dom';
import { Avatar, Button, Modal, StatusBadge } from '@immo/ui-kit';
import type { ErrorCode, GetAdminMemberResponse } from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';
import { env } from '@/lib/env';
import { useTranslations } from '@/i18n/use-translations';
import { BreadcrumbLeading } from '@/components/layout/page-leading';
import { PageTopbar } from '@/components/layout/topbar-slots';
import { MembersIcon, OrganizationsIcon } from '@/components/layout/icons';
import { DetailRow, Section } from '@/components/detail-layout';
import { memberInitials } from '@/lib/member-initials';
import { RequirePermission, useCan } from '@/lib/auth/permissions';
import { MemberActivitySection } from './activity-section';
import { MemberOnboardingSection } from './onboarding-section';
import { MemberEmailsSection } from './emails-section';

const TECHNICAL_ROW = 'grid-cols-[140px_minmax(0,1fr)]';

const LAST_OWNER: ErrorCode = 'identity.member.cannot-deactivate-last-owner';
const ALREADY_DEACTIVATED: ErrorCode = 'identity.member.already-deactivated';
const ALREADY_ACTIVE: ErrorCode = 'identity.member.already-active';

type Translate = (key: string) => string;

const statusErrorMessage = (code: string | undefined, t: Translate, tErrors: Translate): string => {
  if (code === LAST_OWNER) return t('status-errors.last-owner');

  if (code === ALREADY_DEACTIVATED || code === ALREADY_ACTIVE) return tErrors(code);

  return t('status-errors.generic');
};

export async function memberDetailLoader({ params }: LoaderFunctionArgs): Promise<GetAdminMemberResponse> {
  const id = params['id'];

  if (typeof id !== 'string' || id.length === 0) {
    throw new Response('Missing member id', { status: 404 });
  }

  const result = await apiClient.adminMembers.get({ pathParams: { id } });

  if (!result.ok) {
    throw new Response('Failed to load member', { status: result.status });
  }

  return result.body;
}

export function MemberDetailPage() {
  const t = useTranslations('pages.admin.members.detail');
  const tList = useTranslations('pages.admin.members');
  const tErrors = useTranslations('errors');
  const revalidator = useRevalidator();
  const intl = useIntl();
  const member = useLoaderData() as GetAdminMemberResponse;
  const fullName = `${member.firstName} ${member.lastName}`.trim();
  const heading = fullName === '' ? member.email : fullName;
  const canUpdate = useCan('backoffice:identity:member-update');
  const canReadOrganizations = useCan('backoffice:identity:organization-read');
  const canEditAccess = canUpdate && canReadOrganizations;
  const collator = new Intl.Collator(intl.locale);
  const labelsOf = (slugs: ReadonlyArray<string>) =>
    slugs
      .map((slug) => tList(`roles.${slug}`))
      .sort(collator.compare)
      .join(', ');
  const valueOrEmpty = (value: string | null) =>
    value === null || value === '' ? <span className="text-text-placeholder">{t('empty')}</span> : value;
  const [technicalOpen, setTechnicalOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const [deactivateConfirmOpen, setDeactivateConfirmOpen] = useState(false);
  const [statusSubmitting, setStatusSubmitting] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);

  const closeConfirm = () => {
    if (starting) return;
    setConfirmOpen(false);
    setStartError(null);
  };

  const changeStatus = async () => {
    setDeactivateConfirmOpen(false);
    setStatusError(null);
    setStatusSubmitting(true);

    const request = { pathParams: { workspaceId: member.workspaceId, id: member.id }, body: {} };

    try {
      const result =
        member.status === 'active'
          ? await apiClient.adminMembers.deactivate(request)
          : await apiClient.adminMembers.reactivate(request);

      if (result.ok) {
        await revalidator.revalidate();

        return;
      }

      const code = (result.body as { error?: { code?: string } } | undefined)?.error?.code;

      setStatusError(statusErrorMessage(code, t, tErrors));

      if (code === ALREADY_DEACTIVATED || code === ALREADY_ACTIVE) await revalidator.revalidate();
    } catch {
      setStatusError(t('status-errors.generic'));
    } finally {
      setStatusSubmitting(false);
    }
  };

  const startImpersonation = async () => {
    setStarting(true);
    setStartError(null);

    // Open the impersonated session in a NEW tab so the operator keeps the admin
    // area open behind them. The tab has to be opened synchronously inside this
    // click gesture — before the `await` — or the browser's popup blocker rejects
    // it; we point the blank tab at the handoff URL once the code returns, and
    // close it if the start fails.
    const tab = window.open('about:blank', '_blank');

    try {
      const result = await apiClient.impersonation.start({
        body: { memberId: member.id },
      });

      if (result.ok) {
        // api.immoos.X ↔ app.immoos.X (see admin-web-app .env.example). The
        // single-use handoff code is carried in the URL **fragment**, not the
        // query string, so it is never sent in the `Referer` header or written
        // to server/proxy access logs (review #2); the customer app reads it
        // from `location.hash`, exchanges it server-side, and clears it. The
        // opaque operating credential never travels through this redirect.
        const customerAppOrigin = env.apiOrigin.replace('//api.', '//app.');
        const handoffUrl = `${customerAppOrigin}/impersonate#code=${encodeURIComponent(result.body.handoffCode)}`;

        if (tab !== null) {
          // Sever the opener back-reference before the tab loads a page holding
          // the operating credential; `replace` keeps about:blank out of its history.
          tab.opener = null;
          tab.location.replace(handoffUrl);
        } else {
          // Popup blocked — fall back to same-tab navigation so the flow still works.
          window.location.assign(handoffUrl);
        }

        setConfirmOpen(false);

        return;
      }

      tab?.close();
      setStartError(result.status === 403 ? t('impersonate.forbidden-error') : t('impersonate.generic-error'));
    } catch {
      tab?.close();
      setStartError(t('impersonate.generic-error'));
    } finally {
      setStarting(false);
    }
  };

  return (
    <>
      <PageTopbar
        leading={
          <BreadcrumbLeading
            icon={MembersIcon}
            crumbs={[{ label: tList('title'), to: '/workspaces/members' }, { label: heading }]}
          />
        }
        trailing={
          <div className="flex items-center gap-2">
            <Button size="sm" variant="secondary" type="button" onClick={() => setTechnicalOpen(true)}>
              {t('technical-cta')}
            </Button>
            <Button size="sm" variant="secondary" type="button" onClick={() => setConfirmOpen(true)}>
              {t('impersonate-cta')}
            </Button>
            {member.status === 'active' ? (
              <Button
                size="sm"
                variant="secondary"
                type="button"
                disabled={statusSubmitting}
                onClick={() => setDeactivateConfirmOpen(true)}
              >
                {t('deactivate-cta')}
              </Button>
            ) : (
              <Button size="sm" variant="secondary" type="button" disabled={statusSubmitting} onClick={changeStatus}>
                {t('reactivate-cta')}
              </Button>
            )}
            <Button size="sm" asChild>
              <Link to={`/workspaces/members/${member.id}/edit`}>{t('edit-cta')}</Link>
            </Button>
          </div>
        }
      />
      <div className="flex min-h-full flex-col">
        <div className="px-6 pt-6">
          <header className="flex items-start gap-4">
            <Avatar
              className="h-12 w-12 text-lg"
              initials={memberInitials(heading)}
              seed={member.id}
              src={member.avatarUrl ?? undefined}
            />
            <div className="min-w-0 flex-1 space-y-2">
              <h1 className="text-2xl font-bold tracking-tight text-text-primary">{heading}</h1>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-base-sm text-text-tertiary">
                <StatusBadge
                  tone={member.status === 'active' ? 'success' : 'neutral'}
                  label={t(`status.${member.status}`)}
                />
                <span>
                  {member.lastActive === null
                    ? t('last-active-never')
                    : t('last-active', { date: intl.formatDate(member.lastActive, { dateStyle: 'long' }) })}
                </span>
                <span>
                  {member.email} ·{' '}
                  <Link
                    to={`/workspaces/${member.workspaceId}`}
                    className="text-interactive underline-offset-4 hover:underline"
                  >
                    {member.workspaceName}
                  </Link>
                </span>
              </div>
              {statusError !== null && (
                <p role="alert" className="text-sm text-danger-600">
                  {statusError}
                </p>
              )}
            </div>
          </header>
        </div>
        <div className="mt-6 flex-1 border-t border-border px-6">
          <div key={member.id} className="max-w-[880px] space-y-6 pt-6 pb-6">
            <Section collapsible title={t('roles-section.title')} icon={OrganizationsIcon}>
              {member.workspaceRoles.length === 0 && member.organizations.length === 0 ? (
                <p className="text-base-sm text-text-tertiary">{t('roles-section.empty')}</p>
              ) : (
                <dl className="space-y-2 text-base-sm">
                  <DetailRow
                    label={t('roles-section.workspace-roles-label')}
                    value={valueOrEmpty(member.workspaceRoles.length === 0 ? null : labelsOf(member.workspaceRoles))}
                  />
                  {member.organizations.map((organization) => (
                    <DetailRow
                      key={organization.organizationId}
                      label={
                        <>
                          <span className="min-w-0 break-words">{organization.name}</span>
                          {organization.isPrimary && (
                            <StatusBadge size="sm" tone="neutral" label={t('roles-section.primary')} />
                          )}
                        </>
                      }
                      value={valueOrEmpty(organization.roles.length === 0 ? null : labelsOf(organization.roles))}
                    />
                  ))}
                </dl>
              )}
              {canEditAccess && (
                <div className="pt-3">
                  <Button size="sm" variant="secondary" asChild>
                    <Link to="access">{t('roles-section.edit-cta')}</Link>
                  </Button>
                </div>
              )}
            </Section>

            <Section collapsible title={t('profile-section.title')} icon={MembersIcon}>
              <dl className="space-y-2 text-base-sm">
                <DetailRow label={t('profile-section.position-label')} value={valueOrEmpty(member.position)} />
                <DetailRow label={t('profile-section.phone-label')} value={valueOrEmpty(member.phone)} />
                <DetailRow label={t('profile-section.mobile-label')} value={valueOrEmpty(member.mobile)} />
              </dl>
            </Section>

            <MemberOnboardingSection key={member.id} memberId={member.id} revision={member} />

            <RequirePermission permission="backoffice:identity:audit-event-read">
              <MemberActivitySection key={member.id} memberId={member.id} revision={member} />
            </RequirePermission>

            <MemberEmailsSection email={member.email} workspaceId={member.workspaceId} />
          </div>
        </div>
      </div>
      <Modal
        open={technicalOpen}
        onClose={() => setTechnicalOpen(false)}
        size="md"
        title={t('technical-section.title')}
        confirmOnClose={false}
        footer={
          <Button variant="secondary" type="button" onClick={() => setTechnicalOpen(false)}>
            {t('technical-dialog.close')}
          </Button>
        }
      >
        <dl className="space-y-2 text-base-sm">
          <DetailRow
            className={TECHNICAL_ROW}
            label={t('id-label')}
            value={<span className="break-all font-mono text-sm">{member.id}</span>}
          />
          <DetailRow
            className={TECHNICAL_ROW}
            label={t('user-id-label')}
            value={<span className="break-all font-mono text-sm">{member.userId}</span>}
          />
          <DetailRow
            className={TECHNICAL_ROW}
            label={t('created-at-label')}
            value={intl.formatDate(member.createdAt, { dateStyle: 'long' })}
          />
        </dl>
      </Modal>
      <Modal
        open={confirmOpen}
        onClose={closeConfirm}
        size="md"
        title={t('impersonate.title')}
        footer={
          <>
            <Button variant="ghost" type="button" onClick={closeConfirm} disabled={starting}>
              {t('impersonate.cancel')}
            </Button>
            <Button type="button" onClick={startImpersonation} disabled={starting}>
              {starting ? t('impersonate.submitting') : t('impersonate.confirm')}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-base text-text-secondary">{t('impersonate.body', { name: heading })}</p>
          {startError !== null && <p className="text-sm text-danger-600">{startError}</p>}
        </div>
      </Modal>
      <Modal
        open={deactivateConfirmOpen}
        onClose={() => setDeactivateConfirmOpen(false)}
        size="md"
        title={t('deactivate.title', { name: heading })}
        footer={
          <>
            <Button variant="ghost" type="button" onClick={() => setDeactivateConfirmOpen(false)}>
              {t('deactivate.cancel')}
            </Button>
            <Button variant="danger" type="button" disabled={statusSubmitting} onClick={changeStatus}>
              {t('deactivate.confirm')}
            </Button>
          </>
        }
      >
        <p className="text-base text-text-secondary">{t('deactivate.body')}</p>
      </Modal>
      <Outlet />
    </>
  );
}
